import { UseSearchResult } from "@saleor/hooks/makeSearch";
import { findInEnum, findValueInEnum, maybe } from "@saleor/misc";
import { MallFilterData_malls_edges_node } from "@saleor/motherOrders/types/MallFilterData";
import {
  SearchMalls,
  SearchMallsVariables
} from "@saleor/searches/types/SearchMalls";
import {
  PaymentType,
  StoreOrderFilterKeys,
  StoreOrderListFilterOpts
} from "@saleor/storeOrders/components/StoreOrderListPage/filters";

import { IFilterElement } from "../../../components/Filter";
import {
  DeliveryTypeEnum,
  PaymentChannelCodeEnum,
  SmopOrderStatusEnum,
  StoreOrderFilterInput,
  StoreTypeEnum
} from "../../../types/globalTypes";
import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getGteLteVariables,
  getMinMaxQueryParam,
  getMultipleEnumValueQueryParam,
  getMultipleTextFieldQueryParam as getMultiText,
  getMultipleValueQueryParam,
  getSingleEnumValueQueryParam,
  getSingleValueQueryParam
} from "../../../utils/filters";
import {
  StoreOrderListUrlFilters,
  StoreOrderListUrlFiltersEnum,
  StoreOrderListUrlFiltersWithMultipleValuesEnum,
  StoreOrderListUrlQueryParams
} from "../../urls";

export const ORDER_FILTERS_KEY = "orderFilters";

export function getFilterOpts(
  params: StoreOrderListUrlFilters
): StoreOrderListFilterOpts {
  return {
    created: {
      active: maybe(
        () =>
          [params.createdFrom, params.createdTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.createdTo, ""),
        min: maybe(() => params.createdFrom, "")
      }
    },
    customer: {
      active: !!maybe(() => params.customer),
      value: params.customer
    },
    deliveryType: {
      active: maybe(() => params.deliveryType !== undefined, false),
      value: maybe(
        () =>
          dedupeFilter(
            params.deliveryType.map(status =>
              findValueInEnum(status, DeliveryTypeEnum)
            )
          ),
        []
      )
    },
    paymentType: {
      active: maybe(() => params.payment !== undefined, false),
      value: maybe(() => findValueInEnum(params.payment, PaymentType))
    },
    runnerName: {
      active:
        !!maybe(() => params.runnerFirstName) ||
        !!maybe(() => params.runnerLastName),
      value: {
        firstName: params.runnerFirstName,
        lastName: params.runnerLastName
      }
    },
    shipping: {
      active: maybe(
        () =>
          [params.shippingFrom, params.shippingTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.shippingTo, ""),
        min: maybe(() => params.shippingFrom, "")
      }
    },
    status: {
      active: maybe(() => params.status !== undefined, false),
      value: maybe(() => findValueInEnum(status, SmopOrderStatusEnum))
    },
    store: {
      active: !!maybe(() => params.store),
      value: params.store
    },
    verticalType: {
      active: maybe(() => params.verticalType !== undefined, false),
      value: maybe(
        () =>
          dedupeFilter(
            params.verticalType.map(status =>
              findValueInEnum(status, StoreTypeEnum)
            )
          ),
        []
      )
    }
  };
}

export function getFilterOptsWithMall(
  params: StoreOrderListUrlFilters,
  malls: {
    initial: MallFilterData_malls_edges_node[];
    search: UseSearchResult<SearchMalls, SearchMallsVariables>;
  }
): StoreOrderListFilterOpts {
  return {
    created: {
      active: maybe(
        () =>
          [params.createdFrom, params.createdTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.createdTo, ""),
        min: maybe(() => params.createdFrom, "")
      }
    },
    customer: {
      active: !!maybe(() => params.customer),
      value: params.customer
    },
    deliveryType: {
      active: maybe(() => params.deliveryType !== undefined, false),
      value: maybe(
        () =>
          dedupeFilter(
            params.deliveryType.map(status =>
              findValueInEnum(status, DeliveryTypeEnum)
            )
          ),
        []
      )
    },
    mall: {
      active: !!params.mall,
      choices: maybe(
        () =>
          malls.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: params?.mall
        ? maybe(
            () =>
              malls.initial.map(mall => ({
                label: mall.name,
                value: mall.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => malls.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: malls.search.result.loading,
      onFetchMore: malls.search.loadMore,
      onSearchChange: malls.search.search,
      value: maybe(() => dedupeFilter(params.mall), [])
    },
    paymentType: {
      active: maybe(() => params.payment !== undefined, false),
      value: maybe(() => findValueInEnum(params.payment, PaymentType))
    },
    runnerName: {
      active:
        !!maybe(() => params.runnerFirstName) ||
        !!maybe(() => params.runnerLastName),
      value: {
        firstName: params.runnerFirstName,
        lastName: params.runnerLastName
      }
    },
    shipping: {
      active: maybe(
        () =>
          [params.shippingFrom, params.shippingTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.shippingTo, ""),
        min: maybe(() => params.shippingFrom, "")
      }
    },
    status: {
      active: maybe(() => params.status !== undefined, false),
      value: maybe(() => findValueInEnum(status, SmopOrderStatusEnum))
    },
    store: {
      active: !!maybe(() => params.store),
      value: params.store
    },
    verticalType: {
      active: maybe(() => params.verticalType !== undefined, false),
      value: maybe(
        () =>
          dedupeFilter(
            params.verticalType.map(status =>
              findValueInEnum(status, StoreTypeEnum)
            )
          ),
        []
      )
    }
  };
}

export function getFilterVariables(
  params: StoreOrderListUrlFilters
): StoreOrderFilterInput {
  const paymentMethod = params?.payment?.split(":");
  const paymentMethodType =
    paymentMethod !== undefined && paymentMethod.length > 0
      ? paymentMethod[0]
      : null;
  const channelCode =
    paymentMethod !== undefined && paymentMethod.length > 0
      ? maybe(() => [findInEnum(paymentMethod[1], PaymentChannelCodeEnum)])
      : null;

  return {
    brandName: params.store,
    created: getGteLteVariables({
      gte: params.createdFrom,
      lte: params.createdTo
    }),
    customer: params.customer,
    deliveryType: maybe(() =>
      params.deliveryType.map(status => findInEnum(status, DeliveryTypeEnum))
    ),
    malls: params.mall,
    paymentChannelCode: channelCode,
    paymentMethod: paymentMethodType,
    runnerName: {
      firstName: params.runnerFirstName,
      lastName: params.runnerLastName
    },
    search: params.query,
    shipping: getGteLteVariables({
      gte: params.shippingFrom,
      lte: params.shippingTo
    }),
    smopStatus:
      params.status !== undefined
        ? [findValueInEnum(params.status, SmopOrderStatusEnum)]
        : null,
    verticalType: maybe(() =>
      params.verticalType.map(status => findInEnum(status, StoreTypeEnum))
    )
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<StoreOrderFilterKeys>
): StoreOrderListUrlFilters {
  const { name } = filter;

  switch (name) {
    case StoreOrderFilterKeys.created:
      return getMinMaxQueryParam(
        filter,
        StoreOrderListUrlFiltersEnum.createdFrom,
        StoreOrderListUrlFiltersEnum.createdTo
      );

    case StoreOrderFilterKeys.shipping:
      return getMinMaxQueryParam(
        filter,
        StoreOrderListUrlFiltersEnum.shippingFrom,
        StoreOrderListUrlFiltersEnum.shippingTo
      );

    case StoreOrderFilterKeys.status:
      return getSingleEnumValueQueryParam(
        filter,
        StoreOrderListUrlFiltersEnum.status,
        SmopOrderStatusEnum
      );
    case StoreOrderFilterKeys.customer:
      return getSingleValueQueryParam(
        filter,
        StoreOrderListUrlFiltersEnum.customer
      );

    case StoreOrderFilterKeys.deliveryType:
      return getMultipleEnumValueQueryParam(
        filter,
        StoreOrderListUrlFiltersWithMultipleValuesEnum.deliveryType,
        DeliveryTypeEnum
      );

    case StoreOrderFilterKeys.verticalType:
      return getMultipleEnumValueQueryParam(
        filter,
        StoreOrderListUrlFiltersWithMultipleValuesEnum.verticalType,
        StoreTypeEnum
      );

    case StoreOrderFilterKeys.store:
      return getSingleValueQueryParam(
        filter,
        StoreOrderListUrlFiltersEnum.store
      );
    case StoreOrderFilterKeys.paymentType:
      return getSingleEnumValueQueryParam(
        filter,
        StoreOrderListUrlFiltersEnum.payment,
        PaymentType
      );
    case StoreOrderFilterKeys.runnerName:
      return {
        runnerFirstName: filter.active
          ? getMultiText(filter, StoreOrderListUrlFiltersEnum.runnerLastName) &&
            getMultiText(
              filter,
              StoreOrderListUrlFiltersEnum.runnerFirstName
            ) === undefined
            ? ""
            : getMultiText(filter, StoreOrderListUrlFiltersEnum.runnerFirstName)
          : undefined,

        runnerLastName: filter.active
          ? getMultiText(
              filter,
              StoreOrderListUrlFiltersEnum.runnerFirstName
            ) &&
            getMultiText(
              filter,
              StoreOrderListUrlFiltersEnum.runnerLastName
            ) === undefined
            ? ""
            : getMultiText(filter, StoreOrderListUrlFiltersEnum.runnerLastName)
          : undefined
      };

    case StoreOrderFilterKeys.mall:
      return getMultipleValueQueryParam(
        filter,
        StoreOrderListUrlFiltersWithMultipleValuesEnum.mall
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<StoreOrderListUrlFilters>(ORDER_FILTERS_KEY);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  StoreOrderListUrlQueryParams,
  StoreOrderListUrlFilters
>({
  ...StoreOrderListUrlFiltersEnum,
  ...StoreOrderListUrlFiltersWithMultipleValuesEnum
});
